import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";
import CheckoutRecall from "@/components/CheckoutRecall";

const LAST_FORM_KEY = "rieave.checkout.lastForm";

const readLastForm = () => {
  try {
    const raw = localStorage.getItem(LAST_FORM_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeLastForm = (form) => {
  try {
    // Don't persist quantities, items, or anything order-specific. Just the
    // shipping fields that are stable for that buyer.
    const slim = {
      full_name: form.full_name || "",
      email: form.email || "",
      phone: form.phone || "",
      line1: form.line1 || "",
      line2: form.line2 || "",
      city: form.city || "",
      state: form.state || "",
      postal_code: form.postal_code || "",
      country: form.country || "India",
    };
    localStorage.setItem(LAST_FORM_KEY, JSON.stringify(slim));
  } catch {
    /* localStorage might be disabled — silently ignore */
  }
};

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

const Checkout = () => {
  const { items, subtotal, clear, giftProductId } = useCart();
  const { customer } = useAuth();
  const navigate = useNavigate();

  const [payment, setPayment] = useState("cod");
  const [paymentMethods, setPaymentMethods] = useState(null); // null = loading; [] = none enabled (admin error)
  const [coupon, setCoupon] = useState("");
  const [quote, setQuote] = useState(null);
  const [placing, setPlacing] = useState(false);
  const [placed, setPlaced] = useState(false);
  // Coin wallet
  const [wallet, setWallet] = useState(null);
  const [coinsInput, setCoinsInput] = useState(0);
  const [coinPreview, setCoinPreview] = useState({ coins_applied: 0, discount: 0, max_coins_allowed: 0 });

  const [form, setForm] = useState(() => {
    // Priority: logged-in customer details > localStorage from last guest checkout > empty
    const last = !customer ? readLastForm() : null;
    return {
      full_name: customer?.full_name || last?.full_name || "",
      email: customer?.email || last?.email || "",
      phone: customer?.phone || last?.phone || "",
      line1: last?.line1 || "",
      line2: last?.line2 || "",
      city: last?.city || "",
      state: last?.state || "",
      postal_code: last?.postal_code || "",
      country: last?.country || "India",
    };
  });

  // Pincode lookup state — drives the live city/state autofill
  const [pinStatus, setPinStatus] = useState("idle"); // 'idle' | 'loading' | 'ok' | 'invalid'

  useEffect(() => {
    if (placed) return;
    if (items.length === 0) { navigate("/cart"); return; }
    refreshQuote();
    // eslint-disable-next-line
  }, [items, coupon]);

  // Load admin-toggled payment methods so we only show what's enabled
  useEffect(() => {
    api.get("/payment/methods")
      .then((r) => {
        const list = r.data?.methods || [];
        setPaymentMethods(list);
        // Auto-select the first enabled method (preferring the current selection if still valid)
        if (list.length > 0 && !list.find((m) => m.key === payment)) {
          setPayment(list[0].key);
        }
      })
      .catch(() => setPaymentMethods([]));
    // eslint-disable-next-line
  }, []);

  // Load wallet for logged-in customer
  useEffect(() => {
    if (!customer) { setWallet(null); return; }
    api.get("/coins/me").then(r => setWallet(r.data)).catch(() => setWallet(null));
  }, [customer]);

  // Recompute live coin redemption preview whenever input or subtotal changes
  useEffect(() => {
    if (!customer || !wallet || !quote) { setCoinPreview({ coins_applied: 0, discount: 0, max_coins_allowed: 0 }); return; }
    const desired = parseInt(coinsInput || 0, 10);
    if (desired <= 0) { setCoinPreview({ coins_applied: 0, discount: 0, max_coins_allowed: 0 }); return; }
    api.post("/coins/redeem-preview", { coins: desired, subtotal: quote.subtotal })
      .then(r => setCoinPreview(r.data))
      .catch(() => setCoinPreview({ coins_applied: 0, discount: 0, max_coins_allowed: 0 }));
  }, [coinsInput, quote, customer, wallet]);

  // Pincode → city/state auto-fill (debounced). Resets to idle when pincode is invalid length.
  useEffect(() => {
    const pin = (form.postal_code || "").trim();
    if (pin.length !== 6 || !/^\d{6}$/.test(pin)) {
      if (pinStatus !== "idle") setPinStatus("idle");
      return;
    }
    setPinStatus("loading");
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.get(`/checkout/pincode/${pin}`);
        if (data?.valid) {
          setForm((f) => ({
            ...f,
            city: data.city || f.city,
            state: data.state || f.state,
            country: data.country || f.country || "India",
          }));
          setPinStatus("ok");
        } else {
          setPinStatus("invalid");
        }
      } catch {
        setPinStatus("invalid");
      }
    }, 350);
    return () => clearTimeout(handle);
    // eslint-disable-next-line
  }, [form.postal_code]);

  // Apply the recalled address from CheckoutRecall (after OTP verify)
  const applyRecalledForm = (incoming) => {
    setForm((prev) => ({
      ...prev,
      full_name: incoming.full_name || prev.full_name,
      email: incoming.email || prev.email,
      phone: incoming.phone || prev.phone,
      line1: incoming.line1 || prev.line1,
      line2: incoming.line2 || prev.line2,
      city: incoming.city || prev.city,
      state: incoming.state || prev.state,
      postal_code: incoming.postal_code || prev.postal_code,
      country: incoming.country || prev.country || "India",
    }));
  };

  const refreshQuote = async () => {
    try {
      const body = items.map(i => ({ product_id: i.product_id, quantity: i.quantity, size: i.size }));
      const { data } = await api.post("/checkout/quote", body, { params: coupon ? { coupon_code: coupon } : {} });
      setQuote(data);
    } catch { /* ignore */ }
  };

  const applyCoupon = async () => {
    if (!coupon) return;
    try {
      await api.post("/coupons/validate", null, { params: { code: coupon, subtotal } });
      toast.success("Coupon applied");
      refreshQuote();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid coupon");
    }
  };

  const placeOrder = async () => {
    const required = ["full_name", "line1", "city", "state", "postal_code", "phone"];
    const missing = required.filter(k => !form[k]);
    if (missing.length) { toast.error("Please fill the shipping details"); return; }
    if (!form.email && !customer?.email) { toast.error("Email is required for order updates"); return; }

    setPlacing(true);
    try {
      const address = {
        full_name: form.full_name, line1: form.line1, line2: form.line2,
        city: form.city, state: form.state, postal_code: form.postal_code,
        country: form.country, phone: form.phone, is_default: true,
      };
      const body = {
        items: items.map(i => ({ product_id: i.product_id, quantity: i.quantity, size: i.size })),
        shipping_address: address,
        payment_method: payment,
        coupon_code: coupon || null,
        customer_email: form.email || customer?.email,
        customer_phone: form.phone,
        gift_product_id: giftProductId || null,
        coins_redeem: coinPreview.coins_applied || 0,
      };
      const { data } = await api.post("/orders", body);
      const order = data.order;

      // Save form to localStorage so next checkout (even guest) is auto-filled
      writeLastForm(form);

      if (payment === "cod") {
        setPlaced(true);
        navigate(`/checkout/success?order_id=${order.id}`);
        clear();
        return;
      }
      if (payment === "razorpay") {
        if (!data.razorpay?.key_id) {
          toast.error("Razorpay is not configured yet. Please add API keys in backend/.env to enable online payment.");
          return;
        }
        await loadRazorpayScript();
        const options = {
          key: data.razorpay.key_id,
          amount: data.razorpay.amount,
          currency: data.razorpay.currency,
          order_id: data.razorpay.order_id,
          name: "rieave",
          description: `Order ${order.order_number}`,
          prefill: { name: form.full_name, email: form.email || customer?.email, contact: form.phone },
          theme: { color: "#C9789A" },
          handler: async (resp) => {
            try {
              await api.post("/payments/razorpay/verify", {
                order_id: order.id,
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
              });
              setPlaced(true);
              navigate(`/checkout/success?order_id=${order.id}`);
              clear();
            } catch {
              toast.error("Payment verification failed");
            }
          },
        };
        new window.Razorpay(options).open();
        return;
      }
      if (payment === "stripe") {
        if (data.stripe?.url) { window.location.href = data.stripe.url; return; }
        toast.error("Stripe is not configured yet. Please add STRIPE_SECRET_KEY in backend/.env.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not place order");
    } finally { setPlacing(false); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const total = Math.max(0, (quote?.total ?? subtotal) - (coinPreview.discount || 0));

  return (
    <div className="rieave-container py-12 md:py-16" data-testid="checkout-page">
      <p className="eyebrow mb-2">Checkout</p>
      <h1 className="font-hero text-4xl md:text-5xl mb-10">Almost <span className="serif-italic text-rose">yours.</span></h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-8">
          {/* Returning customer recall (OTP-gated, hidden when logged in) */}
          {!customer && <CheckoutRecall onRecalled={applyRecalledForm} />}

          {/* Contact */}
          <section className="bg-white rounded-2xl border border-border p-6">
            <p className="eyebrow mb-4">Contact</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input placeholder="Full name" autoComplete="name" value={form.full_name} onChange={set("full_name")} className="h-11 rounded-xl" data-testid="checkout-name" />
              <Input placeholder="Phone" type="tel" inputMode="tel" autoComplete="tel" value={form.phone} onChange={set("phone")} className="h-11 rounded-xl" data-testid="checkout-phone" />
              <Input placeholder="Email" type="email" autoComplete="email" value={form.email} onChange={set("email")} className="h-11 rounded-xl md:col-span-2" data-testid="checkout-email" />
            </div>
          </section>

          {/* Shipping */}
          <section className="bg-white rounded-2xl border border-border p-6">
            <p className="eyebrow mb-4">Shipping address</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input placeholder="Address line 1" autoComplete="address-line1" value={form.line1} onChange={set("line1")} className="h-11 rounded-xl md:col-span-2" data-testid="checkout-line1" />
              <Input placeholder="Address line 2 (optional)" autoComplete="address-line2" value={form.line2} onChange={set("line2")} className="h-11 rounded-xl md:col-span-2" />

              {/* Pincode FIRST — typing it auto-fills city + state below */}
              <div className="md:col-span-2 relative">
                <Input
                  placeholder="Postal code (6 digits) — type to auto-fill city & state"
                  autoComplete="postal-code"
                  inputMode="numeric"
                  maxLength={6}
                  value={form.postal_code}
                  onChange={(e) => setForm({ ...form, postal_code: e.target.value.replace(/\D/g, "").slice(0, 6) })}
                  className="h-11 rounded-xl pr-12"
                  data-testid="checkout-postal"
                />
                {pinStatus === "loading" && (
                  <Loader2
                    size={16}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-mauve animate-spin"
                    data-testid="pincode-loading"
                  />
                )}
                {pinStatus === "ok" && (
                  <span
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-emerald-600"
                    data-testid="pincode-ok"
                  >
                    <Check size={18} strokeWidth={2.5} />
                  </span>
                )}
                {pinStatus === "invalid" && (
                  <span
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-rose text-xs font-label uppercase tracking-[0.18em]"
                    data-testid="pincode-invalid"
                  >
                    Invalid
                  </span>
                )}
              </div>

              <Input
                placeholder="City"
                autoComplete="address-level2"
                value={form.city}
                onChange={set("city")}
                className="h-11 rounded-xl"
                data-testid="checkout-city"
              />
              <Input
                placeholder="State"
                autoComplete="address-level1"
                value={form.state}
                onChange={set("state")}
                className="h-11 rounded-xl"
                data-testid="checkout-state"
              />
              <Input
                placeholder="Country"
                autoComplete="country-name"
                value={form.country}
                onChange={set("country")}
                className="h-11 rounded-xl md:col-span-2"
                data-testid="checkout-country"
              />
            </div>
          </section>

          {/* Payment */}
          <section className="bg-white rounded-2xl border border-border p-6">
            <p className="eyebrow mb-4">Payment method</p>
            {paymentMethods === null ? (
              <p className="text-sm text-mauve" data-testid="payment-loading">Loading payment options…</p>
            ) : paymentMethods.length === 0 ? (
              <div className="rounded-xl border border-rose/40 bg-rose/10 p-4 text-sm text-rose" data-testid="payment-none-enabled">
                No payment methods are currently available. Please contact us at{" "}
                <a href="mailto:info@rieave.com" className="underline">info@rieave.com</a>.
              </div>
            ) : (
              <div className="space-y-3">
                {paymentMethods.map((opt) => (
                  <label
                    key={opt.key}
                    className={`flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition ${
                      payment === opt.key
                        ? "border-charcoal bg-cream"
                        : "border-border bg-white hover:border-charcoal/60"
                    }`}
                    data-testid={`payment-option-${opt.key}`}
                  >
                    <input
                      type="radio"
                      name="payment"
                      checked={payment === opt.key}
                      onChange={() => setPayment(opt.key)}
                      className="accent-rose"
                    />
                    <div>
                      <p className="font-cormorant text-lg">{opt.label}</p>
                      {opt.description && (
                        <p className="text-xs text-mauve">{opt.description}</p>
                      )}
                      {opt.key === "cod" && opt.min_order > 0 && (
                        <p className="text-[11px] text-mauve mt-0.5">
                          Available on orders ₹{Number(opt.min_order).toLocaleString("en-IN")}+
                        </p>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            )}
          </section>
        </div>

        <aside className="bg-white rounded-2xl border border-border p-6 h-fit lg:sticky lg:top-24" data-testid="checkout-summary">
          <p className="eyebrow mb-4">Your order</p>
          <div className="space-y-3 max-h-60 overflow-y-auto mb-4">
            {items.map(it => (
              <div key={`${it.product_id}-${it.size || "_"}`} className="flex gap-3">
                <div className="w-12 h-16 rounded-lg overflow-hidden bg-blush/40 flex-shrink-0">
                  {it.image && <img src={it.image} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 flex justify-between items-center text-sm">
                  <div>
                    <p className="font-cormorant text-base leading-tight">{it.name}</p>
                    <p className="font-label text-[10px] uppercase tracking-[0.2em] text-mauve">Qty {it.quantity}{it.size ? ` · ${it.size}` : ""}</p>
                  </div>
                  <p>{formatINR(it.price * it.quantity)}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="flex gap-2 mb-5">
            <Input placeholder="Coupon code" value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} className="h-10 rounded-full" data-testid="checkout-coupon-input" />
            <button className="btn-outline py-2 px-5" onClick={applyCoupon} data-testid="checkout-apply-coupon">Apply</button>
          </div>

          {customer && wallet && wallet.balance > 0 && (
            <div className="mb-5 p-4 rounded-xl bg-gradient-to-br from-rose/10 to-lavender/30 border border-rose/20" data-testid="checkout-coins-section">
              <div className="flex items-center justify-between mb-2">
                <p className="font-label text-[11px] uppercase tracking-[0.22em]">Use {wallet.settings?.coin_name || "coins"}</p>
                <p className="text-xs text-mauve">Balance: <strong>{wallet.balance}</strong></p>
              </div>
              <div className="flex gap-2">
                <Input
                  type="number"
                  placeholder="Coins to redeem"
                  value={coinsInput || ""}
                  min={0}
                  max={wallet.balance}
                  onChange={(e) => setCoinsInput(e.target.value.replace(/[^0-9]/g, ""))}
                  className="h-10 rounded-full"
                  data-testid="checkout-coins-input"
                />
                <button
                  className="btn-outline py-2 px-4 whitespace-nowrap"
                  onClick={() => setCoinsInput(coinPreview.max_coins_allowed || 0)}
                  data-testid="checkout-coins-max"
                >
                  Max ({coinPreview.max_coins_allowed || 0})
                </button>
              </div>
              {coinPreview.coins_applied > 0 && (
                <p className="text-xs text-emerald-700 mt-2" data-testid="checkout-coins-preview">
                  ✓ {coinPreview.coins_applied} coins → {formatINR(coinPreview.discount)} off
                </p>
              )}
            </div>
          )}

          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span>{formatINR(quote?.subtotal ?? subtotal)}</span></div>
            {quote?.discount > 0 && <div className="flex justify-between text-rose"><span>Discount ({quote.coupon_code})</span><span>- {formatINR(quote.discount)}</span></div>}
            {coinPreview.discount > 0 && (
              <div className="flex justify-between text-rose" data-testid="checkout-coin-discount">
                <span>Coins ({coinPreview.coins_applied})</span>
                <span>- {formatINR(coinPreview.discount)}</span>
              </div>
            )}
            <div className="flex justify-between"><span>Shipping</span><span>{(quote?.shipping_fee ?? 0) === 0 ? "Free" : formatINR(quote?.shipping_fee)}</span></div>
          </div>
          <hr className="my-4 border-border" />
          <div className="flex justify-between items-baseline mb-5">
            <span className="font-label text-sm uppercase tracking-[0.22em]">Total</span>
            <span className="font-hero text-3xl" data-testid="checkout-total">{formatINR(total)}</span>
          </div>
          <button
            className="btn-primary w-full"
            onClick={placeOrder}
            disabled={placing}
            data-testid="place-order-button"
          >
            {placing ? "Placing order…" : `Place order · ${formatINR(total)}`}
          </button>
          <p className="text-[11px] text-mauve text-center mt-3 inline-flex items-center gap-1.5 justify-center w-full"><Check size={12} className="text-rose" /> Secure checkout · Easy returns</p>
        </aside>
      </div>
    </div>
  );
};

export default Checkout;
